import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSocialMediaPlatforms } from "../../../services/api";
import { ALLOWED_PLATFORMS, FormErrors, SocialLink, UpdateProfileUIProps } from "../../../types/updateProfileProps";
import { AddPill, Card, FormActions, SelectField, TextField, isHttpUrl } from "./formPrimitives";

const URL_MAX_LENGTH = 5000;

type PlatformStyle = { name: string; color: string };

/** Brand names are not translated */
const PLATFORM_STYLES: Record<string, PlatformStyle> = {
    YOUTUBE: { name: "YouTube", color: "#ED1D24" },
    FACEBOOK: { name: "Facebook", color: "#1877F2" },
    TWITTER: { name: "Twitter", color: "#000000" },
    INSTAGRAM: { name: "Instagram", color: "#E1306C" },
    LINKEDIN: { name: "LinkedIn", color: "#0A66C2" },
};

const FALLBACK_STYLE: PlatformStyle = { name: "Link", color: "#6B7280" };

const GLYPH_LETTERS: Record<string, string> = { FACEBOOK: "f", TWITTER: "X", LINKEDIN: "in" };

const getPlatformStyle = (type: string): PlatformStyle => PLATFORM_STYLES[type] ?? FALLBACK_STYLE;

/** "https://instagram.com/janez98?x=1" -> "@janez98" */
const getHandle = (url: string) => {
    const parts = url.split(/[?#]/)[0].split("/").filter(Boolean);
    if (parts.length <= 2) return url;

    const last = parts[parts.length - 1];
    return last.startsWith("@") ? last : `@${last}`;
};

const PlatformGlyph: React.FC<{ type: string; color: string }> = ({ type, color }) => {
    if (type === "YOUTUBE") {
        return (
            <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
                <rect x="3" y="7" width="26" height="18" rx="5" fill="#FFFFFF" />
                <path d="M13 12v8l7-4z" fill={color} />
            </svg>
        );
    }

    if (type === "INSTAGRAM") {
        return (
            <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
                <rect x="6" y="6" width="20" height="20" rx="6" fill="none" stroke="#FFFFFF" strokeWidth="2.4" />
                <circle cx="16" cy="16" r="4.6" fill="none" stroke="#FFFFFF" strokeWidth="2.4" />
                <circle cx="22" cy="10" r="1.4" fill="#FFFFFF" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
            <text
                x="16"
                y="23"
                textAnchor="middle"
                fontSize="20"
                fontWeight="700"
                fontFamily="Arial, Helvetica, sans-serif"
                fill="#FFFFFF"
            >
                {GLYPH_LETTERS[type] ?? "?"}
            </text>
        </svg>
    );
};

/** 64px coloured square from the Figma social card */
const PlatformBadge: React.FC<{ type: string }> = ({ type }) => {
    const style = getPlatformStyle(type);

    return (
        <div
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: style.color }}
        >
            <PlatformGlyph type={type} color={style.color} />
        </div>
    );
};

type Mode = "idle" | "add" | number;

const SocialMediaSection: React.FC<UpdateProfileUIProps> = ({ socialLinks, commitSocialLinks, isPersisting }) => {
    const { t } = useTranslation("updateProfile");

    const [platformOptions, setPlatformOptions] = useState<string[]>(ALLOWED_PLATFORMS);
    const [mode, setMode] = useState<Mode>("idle");
    const [platform, setPlatform] = useState("");
    const [url, setUrl] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});

    // The backend only accepts the platforms in ALLOWED_PLATFORMS, so the DB list is narrowed to those.
    useEffect(() => {
        let isActive = true;

        const loadPlatforms = async () => {
            try {
                const list = await fetchSocialMediaPlatforms();
                const supported = list
                    .map((item) => item.toUpperCase())
                    .filter((item) => ALLOWED_PLATFORMS.includes(item));
                if (isActive && supported.length > 0) setPlatformOptions(supported);
            } catch (err) {
                console.error("Failed to load social media platforms", err);
            }
        };

        loadPlatforms();
        return () => {
            isActive = false;
        };
    }, []);

    const editingIndex = typeof mode === "number" ? mode : -1;

    // one link per platform: hide the platforms already used by the other saved links
    const availablePlatforms = platformOptions.filter(
        (option) => !socialLinks.some((link, index) => link.type === option && index !== editingIndex)
    );
    const canAddMore = mode === "idle" && availablePlatforms.length > 0;

    const platformSelectOptions = availablePlatforms.map((option) => ({
        value: option,
        label: getPlatformStyle(option).name,
    }));

    const openAddForm = () => {
        setPlatform("");
        setUrl("");
        setErrors({});
        setMode("add");
    };

    const openEditForm = (index: number) => {
        setPlatform(socialLinks[index].type);
        setUrl(socialLinks[index].url);
        setErrors({});
        setMode(index);
    };

    const closeForm = () => {
        setMode("idle");
        setPlatform("");
        setUrl("");
        setErrors({});
    };

    const validate = (trimmedUrl: string): FormErrors => {
        const formErrors: FormErrors = {};

        if (!platform) formErrors.platform = t("page.errors.required");

        if (!trimmedUrl) {
            formErrors.url = t("page.errors.required");
        } else if (!isHttpUrl(trimmedUrl)) {
            formErrors.url = t("page.errors.invalidUrl");
        } else if (trimmedUrl.length > URL_MAX_LENGTH) {
            formErrors.url = t("page.errors.maxLength", { max: URL_MAX_LENGTH });
        }

        return formErrors;
    };

    const submit = async () => {
        const trimmedUrl = url.trim();
        const formErrors = validate(trimmedUrl);
        setErrors(formErrors);
        if (Object.keys(formErrors).length > 0) return;

        const link: SocialLink = { type: platform, url: trimmedUrl };
        const nextLinks =
            mode === "add" ? [...socialLinks, link] : socialLinks.map((item, index) => (index === mode ? link : item));

        const isSaved = await commitSocialLinks(nextLinks);
        if (isSaved) closeForm();
    };

    const removeLink = (index: number) => {
        commitSocialLinks(socialLinks.filter((_, itemIndex) => itemIndex !== index));
    };

    const renderForm = () => (
        <Card className="flex flex-col gap-3">
            <SelectField
                name="socialPlatform"
                label={t("page.socialPlatform")}
                value={platform}
                placeholder={t("page.selectPlatform")}
                options={platformSelectOptions}
                onValueChange={(value) => {
                    setPlatform(value);
                    setErrors((previous) => ({ ...previous, platform: "" }));
                }}
                error={errors.platform}
            />
            <TextField
                name="socialUrl"
                label={t("page.profileLink")}
                value={url}
                maxLength={URL_MAX_LENGTH}
                placeholder={t("page.profileLinkPlaceholder")}
                hint={t("page.profileLinkExample")}
                onValueChange={(value) => {
                    setUrl(value);
                    setErrors((previous) => ({ ...previous, url: "" }));
                }}
                error={errors.url}
            />
            <FormActions
                cancelLabel={t("page.cancel")}
                confirmLabel={mode === "add" ? t("page.add") : t("page.update")}
                busy={isPersisting}
                onCancel={closeForm}
                onConfirm={submit}
            />
        </Card>
    );

    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm font-medium leading-5 text-gray-900">{t("page.socialMediaLinks")}</p>

            {canAddMore && (
                <AddPill onClick={openAddForm} disabled={isPersisting}>
                    {t("page.addSocialMediaLink")}
                </AddPill>
            )}

            {socialLinks.map((link, index) => {
                if (index === editingIndex) {
                    return <React.Fragment key={`edit-${link.type}`}>{renderForm()}</React.Fragment>;
                }

                const style = getPlatformStyle(link.type);

                return (
                    <Card key={link.type} className="flex items-center gap-4">
                        <PlatformBadge type={link.type} />
                        <div className="flex min-w-0 flex-1 flex-col">
                            <span className="text-base font-bold leading-6 text-gray-900">{style.name}</span>
                            {isHttpUrl(link.url) ? (
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="truncate text-xs text-[#0485F7] hover:underline"
                                >
                                    {getHandle(link.url)}
                                </a>
                            ) : (
                                <span className="truncate text-xs text-gray-500">{link.url}</span>
                            )}
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                            <button
                                type="button"
                                aria-label={t("page.remove")}
                                disabled={isPersisting}
                                onClick={() => removeLink(index)}
                                className="text-gray-900 transition-colors hover:text-red-600 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                aria-label={t("page.edit")}
                                disabled={isPersisting}
                                onClick={() => openEditForm(index)}
                                className="text-gray-900 transition-colors hover:text-blue-600 disabled:opacity-50"
                            >
                                <PencilIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </Card>
                );
            })}

            {mode === "add" && renderForm()}
        </div>
    );
};

export default SocialMediaSection;
